import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toastSuccess, toastError } from '../../utils/alertHelper';
import CommonModal from '../../components/commonComponent/CommonModal';
import axios from '../../utils/axiosInstance';

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

function Nav({ onMenuToggle }) {
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // 'profile' or 'organization'
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user") || "{}"));
  const [notificationCount, setNotificationCount] = useState(5); // Replace with actual API call

  // Helper to check if a value is a File-like object
  const isFile = v =>
    v instanceof File ||
    (v && typeof v === "object" && typeof v.type === "string" && typeof v.size === "number");

  // Format a date-like value into `YYYY-MM-DD` for <input type="date"> fields
  const formatDateForInput = (dateVal) => {
    if (!dateVal) return "";
    try {
      // If it's already a plain YYYY-MM-DD string, return it
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateVal))) return String(dateVal);
      const d = new Date(dateVal);
      if (Number.isNaN(d.getTime())) {
        // fallback: try to take first 10 chars (ISO-like)
        return String(dateVal).slice(0, 10);
      }
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } catch (e) {
      return "";
    }
  };

  // Normalize image values coming from API/localStorage
  const normalizeApiPathValue = (val) => {
    if (!val) return "";
    if (Array.isArray(val)) return val[0] || "";
    if (typeof val === "object") {
      // common shapes: { path: "..."} or { url: "..." }
      if (val.path) return val.path;
      if (val.url) return val.url;
      // fallback to JSON string
      try { return JSON.stringify(val); } catch (e) { return String(val); }
    }
    return String(val);
  };

  const resolveImageUrl = (path) => {
    if (!path) return null;
    const p = String(path).trim();
    if (!p) return null;
    if (p.startsWith("http") || p.startsWith("data:")) return p;
    // strip leading slashes and prepend base
    const cleaned = p.replace(/^\/+/, "");
    return `${IMAGE_BASE_URL}${cleaned}`;
  };

  // If we have a resolved URL (or any URL containing the server path), extract the server-relative path
  const extractRelativePathFromUrl = (url) => {
    if (!url) return "";
    try {
      const s = String(url);
      // If it already looks like a relative path (no protocol), return as-is
      if (!/^https?:\/\//i.test(s) && !s.startsWith("data:")) return s;
      // If the URL contains the IMAGE_BASE_URL, remove that prefix
      if (IMAGE_BASE_URL && s.startsWith(IMAGE_BASE_URL)) {
        return s.slice(IMAGE_BASE_URL.length).replace(/^\/+/, "");
      }
      // As fallback, try to find known folder like 'solar/' and return from there
      const idx = s.indexOf("solar/");
      if (idx !== -1) return s.slice(idx);
      // Last-resort: return the pathname portion of the URL
      try {
        const u = new URL(s);
        return u.pathname.replace(/^\/+/, "");
      } catch (e) {
        return s;
      }
    } catch (e) {
      return "";
    }
  };

  // Fetch fresh user data from server and update localStorage + state
  const fetchAndUpdateUser = useCallback(async () => {
    try {
      const storedUserRaw = localStorage.getItem("user");
      // if (!storedUserRaw) return;
       const storedUser = JSON.parse(storedUserRaw || "{}");
       const userId = storedUser._id || storedUser.id || localStorage.getItem("userId");
      // if (!userId) return;
      const res = await axios.get(`/api/user/${userId}`);
      const data = res.data;
      if (data && data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
      }
    } catch (err) {
      // don't spam users with errors when simply trying to refresh silently
      console.error("fetchAndUpdateUser error:", err);
      toastError("Failed to refresh user info.");
    }
  }, []);

  // Listen for 'userChanged' events dispatched after login/update so Nav updates immediately
  useEffect(() => {
    const onUserChanged = () => {
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        if (stored && (stored._id || stored.id)) {
          setUser(stored);
          // also refetch authoritative server version (in case server returned relative image path)
          fetchAndUpdateUser();
        } else {
          // fallback: try to fetch from server if local storage doesn't have complete user
          fetchAndUpdateUser();
        }
      } catch (e) {
        fetchAndUpdateUser();
      }
    };
    window.addEventListener('userChanged', onUserChanged);
    return () => window.removeEventListener('userChanged', onUserChanged);
  }, [fetchAndUpdateUser]);

  // fetch countries once
  useEffect(() => {
    async function fetchCountries() {
      try {
        const res = await axios.get("admin/api/country");
        const countries = res.data?.countries?.map(c => ({ value: c._id, label: c.name })) || [];
        setCountryOptions(countries);
      } catch (err) {
        console.error("fetchCountries error:", err);
        toastError("Failed to load countries.");
      }
    }
    fetchCountries();
  }, []);

  useEffect(() => {
    async function fetchStates() {
      if (formValues.country) {
        try {
          const res = await axios.get(`admin/api/state?country_id=${formValues.country}`);
          const states = res.data?.states?.map(s => ({ value: s._id, label: s.name })) || [];
          setStateOptions(states);
        } catch (err) {
          console.error("fetchStates error:", err);
          toastError("Failed to load states.");
        }
      } else {
        setStateOptions([]);
      }
      setCityOptions([]); 
    }
    fetchStates();
  }, [formValues.country]);

  useEffect(() => {
    async function fetchCities() {
      if (formValues.state) {
        try {
          const res = await axios.get(`admin/api/city?state_id=${formValues.state}`);
          const cities = res.data?.cities?.map(city => ({ value: city._id, label: city.name })) || [];
          setCityOptions(cities);
        } catch (err) {
          console.error("fetchCities error:", err);
          toastError("Failed to load cities.");
        }
      } else {
        setCityOptions([]);
      }
    }
    fetchCities();
  }, [formValues.state]);

  const openProfileModal = async () => {
    setModalType("profile");
    setModalOpen(true);
    setFormErrors({});
    setFormValues({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      mobile: user.mobileNumber || '',
      email: user.email || '',
      birthDate: formatDateForInput(user.birthDate || user.dob || user.dateOfBirth),
      position: user.role?.name || '',
      // If no uploaded profile, show local default preview; do not treat default as an upload
      profileImage: user.profilepic ? resolveImageUrl(user.profilepic) : "/assets/images/s.png",
    });
  };

  const openOrgModal = async () => {
    setModalType("organization");
    setModalOpen(true);
    setFormErrors({});
    setFormValues({
      companyName: user.business?.name || '',
      websiteURL: user.business?.websiteURL || '',
      mobile: user.business?.mobileNumber || '',
      email: user.business?.email || '',
      gstn: user.business?.gstNumber || '',
      cin: user.business?.cin || '',
      pan: user.business?.pan || '',
      tan: user.business?.tan || '',
      buildingno: user.business?.buildingno || '',
      buildingName: user.business?.buildingName || '',
      landmark: user.business?.landmark || '',
      area: user.business?.area || '',
      city: user.business?.city?._id || user.business?.city || '',
      state: user.business?.state?._id || user.business?.state || '',
      country: user.business?.country?._id || user.business?.country || '',
      pincode: user.business?.pincode || '',
      // Show existing company logo or local default preview
      orgLogo: (user.companyLogo || user.company_logo || user.business?.companyLogo || user.business?.logo)
        ? resolveImageUrl(normalizeApiPathValue(user.companyLogo || user.company_logo || user.business?.companyLogo || user.business?.logo))
        : "/assets/images/solar.png",
    });
  };

  const handleFormChange = (vals) => {
    setFormValues(vals);
  };

  // Simple validation for modal
  const validateForm = () => {
    let errors = {};
    if (modalType === "profile") {
      if (!formValues.firstName) errors.firstName = "First name required";
      if (!formValues.email) errors.email = "Email required";
    } else {
      if (!formValues.companyName) errors.companyName = "Company name required";
      if (!formValues.email) errors.email = "Email required";
      if (!formValues.gstn) errors.gstn = "GSTN required";
      if (!formValues.country) errors.country = "Country required";
      if (!formValues.state) errors.state = "State required";
      if (!formValues.city) errors.city = "City required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit profile/org edit
  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = storedUser._id || storedUser.id || localStorage.getItem("userId");
      if (!userId) {
        toastError("User not found locally.");
        return;
      }
      let apiRes;
      if (modalType === "profile") {
        const payload = {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          mobileNumber: formValues.mobile,
          email: formValues.email,
          dob: formValues.birthDate,
          position: formValues.position,
        };
        if (typeof formValues.profileImage === 'string' && formValues.profileImage && formValues.profileImage !== "/assets/images/s.png") {
          payload.profilepic = extractRelativePathFromUrl(formValues.profileImage);
        }
        let body = payload;
        let config = {};
        if (isFile(formValues.profileImage)) {
          const fd = new FormData();
          Object.entries(payload).forEach(([key, val]) => fd.append(key, val));
          fd.append("profilepic", formValues.profileImage);
          body = fd;
          config = { headers: { "Content-Type": "multipart/form-data" } };
        }
        apiRes = await axios.put(`/api/user/${userId}`, body, config);
      } else {
        const businessPayload = {
          name: formValues.companyName,
          email: formValues.email,
          address: formValues.address,
          buildingno: formValues.buildingno,
          buildingName: formValues.buildingName,
          landmark: formValues.landmark,
          area: formValues.area,
          mobileNumber: formValues.mobile,
          gstNumber: formValues.gstn,
          pincode: formValues.pincode,
          cin: formValues.cin,
          pan: formValues.pan,
          tan: formValues.tan,
          websiteURL: formValues.websiteURL,
          country: formValues.country,
          state: formValues.state,
          city: formValues.city,
        };
        // If orgLogo is a URL string (preview) and not a File, include the server-relative path
        // but do NOT include the local default asset path
        if (typeof formValues.orgLogo === 'string' && formValues.orgLogo && formValues.orgLogo !== "/assets/images/solar.png") {
          businessPayload.logo = extractRelativePathFromUrl(formValues.orgLogo);
        }
        let body = { business: businessPayload };
        let config = {};
        if (isFile(formValues.orgLogo)) {
          const fd = new FormData();
          fd.append("businesslogo", formValues.orgLogo);
          fd.append("business", JSON.stringify(businessPayload));
          body = fd;
          config = { headers: { "Content-Type": "multipart/form-data" } };
        }
        apiRes = await axios.put(`/api/user/${userId}`, body, config);
      }

      const success = apiRes?.data && (apiRes.data.Status === 1 || apiRes.data.Status === true);
      if (success) {
        toastSuccess(`${modalType === "profile" ? "Profile" : "Organization"} updated successfully!`);
        setModalOpen(false);

        // If server returned updated user object, use it immediately; otherwise re-fetch
        if (apiRes.data.user) {
          localStorage.setItem("user", JSON.stringify(apiRes.data.user));
          setUser(apiRes.data.user);
          try { window.dispatchEvent(new Event('userChanged')); } catch (e) { }
        } else {
          // fetch fresh user from server to pick up server-side image paths
          await fetchAndUpdateUser();
        }
      } else {
        toastError(apiRes.data?.Message || "Update failed");
      }
    } catch (err) {
      console.error("handleFormSubmit error:", err);
      toastError("Update failed. Please try again!");
    }
  };

  // Logout logic
  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.clear();
    toastSuccess("Logout successful");
    navigate("/");
  };
  
  // Use IMAGE_BASE_URL for profile picture and company logo (relative path)
  const rawProfile = normalizeApiPathValue(user.profilepic || user.profilePic || user.profile_pic);
  const rawLogo = normalizeApiPathValue(
    user.companyLogo || user.company_logo || user.business?.companyLogo || user.business?.logo || user.business?.company_logo
  );
  const profilePic =
    resolveImageUrl(rawProfile) || "/assets/images/s.png";

  const companyLogo =
    resolveImageUrl(rawLogo) || "/assets/images/solar.png";

  const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim();

  // Determine if current user can view/edit organization profile
  const canEditOrg = (() => {
    try {
      const roleFromUser = user?.role?.name || user?.role || '';
      const roleFromCheck = localStorage.getItem('checkRole') || localStorage.getItem('role') || '';
      const role = String(roleFromUser || roleFromCheck || '').toLowerCase();
      return /super|company|admin|owner/i.test(role);
    } catch (e) {
      return false;
    }
  })();

  // Modal fields arrays
  const profileFields = [
    { name: "firstName", label: "First Name", type: "text" },
    { name: "lastName", label: "Last Name", type: "text" },
    { name: "mobile", label: "Mobile Number", type: "phone" },
    { name: "email", label: "Email Id", type: "email" },
    { name: "birthDate", label: "Date Of Birth", type: "date" },
    { name: "position", label: "Position", type: "text" },
    { name: "profileImage", label: "Upload Profile", type: "file", accept: "image/*" }
  ];

  const orgFields = [
    { name: "companyName", label: "Company Name", type: "text" },
    { name: "websiteURL", label: "Website", type: "text" },
    { name: "mobile", label: "Mobile Number", type: "phone" },
    { name: "email", label: "Email Id", type: "email" },
    { name: "gstn", label: "GSTN", type: "text" },
    { name: "cin", label: "CIN", type: "text" },
    { name: "pan", label: "PAN", type: "text" },
    { name: "tan", label: "TAN", type: "text" },
    { name: "buildingno", label: "Building No / House/Flat", type: "text" },
    { name: "buildingName", label: "Building / Society Name", type: "text" },
    { name: "landmark", label: "Landmark/Nearby", type: "text" },
    { name: "area", label: "Area", type: "text" },
    { name: "city", label: "City", type: "select", options: cityOptions },
    { name: "state", label: "State", type: "select", options: stateOptions },
    { name: "country", label: "Country", type: "select", options: countryOptions },
    { name: "pincode", label: "Pin Code", type: "text" },
    { name: "orgLogo", label: "Upload Logo", type: "file", accept: "image/*" }
  ];

  return (
    <div className="navbar-header" style={{ padding: "20px 13px", height: "60px" }}>
      <div className="navbar-brand-box d-flex align-items-left">
        <a href="#" className="logo">
          <span>
            <img src={companyLogo} alt="" height="50" width="120"/>
          </span>
          <i>
            <img src={companyLogo} alt="" height="50" width="120" />
          </i>
        </a>
        <button
          type="button"
          className="btn btn-sm mr-2 px-3 font-size-16 header-item waves-effect waves-dark mt-3"
          id="vertical-menu-btn"
          onClick={onMenuToggle}
        >
          <i className="fa fa-fw fa-bars"></i>
        </button>
      </div>

      <div className="d-flex align-items-center">
        <div className="dropdown d-inline-block me-2">
          <button
            type="button"
            className="btn header-item noti-icon waves-effect waves-dark"
            id="page-header-add-dropdown"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            style={{ fontSize: "14px", padding: "6px 10px" }}
          >
            <i className="mdi mdi-plus" style={{ fontSize: "18px" }}></i>
          </button>
          <div
            className="dropdown-menu dropdown-menu-sm dropdown-menu-right p-1"
            aria-labelledby="page-header-add-dropdown"
            style={{ minWidth: "180px" }}
          >
            <div className="p-1">
              <div className="row align-items-center mb-1">
                <div className="col">
                  <a
                    href="/user/lead/create" 
                    className="text-decoration-none text-dark"
                    style={{ fontSize: "13px", padding: "4px 8px", display: "block" }}
                  >
                    Add Lead
                  </a>
                </div>
              </div>
              <div className="row align-items-center mb-1">
                <div className="col">
                  <a
                    href="/user/quotation/create" 
                    className="text-decoration-none text-dark"
                    style={{ fontSize: "13px", padding: "4px 8px", display: "block" }}
                  >
                    Add Quotation
                  </a>
                </div>
              </div>
              <div className="row align-items-center mb-1">
                <div className="col">
                  <a
                    href="/user/visit"
                    className="text-decoration-none text-dark"
                    style={{ fontSize: "13px", padding: "4px 8px", display: "block" }}
                  >
                    Add Visit
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dropdown d-inline-block me-2">
          <button
            type="button"
            className="btn header-item noti-icon waves-effect waves-dark position-relative"
            id="page-header-notifications-dropdown"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            style={{ fontSize: "14px", padding: "6px 10px" }}
          >
            <i className="mdi mdi-bell" style={{ fontSize: "18px" }}></i>
          </button>
          <div
            className="dropdown-menu dropdown-menu-lg dropdown-menu-right p-0"
            aria-labelledby="page-header-notifications-dropdown"
            style={{ minWidth: "300px" }}
          >
            <div className="p-3">
              <div className="row align-items-center">
                <div className="col">
                  <h6 className="m-0">Notifications</h6>
                </div>
                <div className="col-auto">
                  <a href="#!" className="small">Mark all as read</a>
                </div>
              </div>
            </div>
            <div className="p-2 border-top text-center">
              <a className="btn btn-sm btn-link font-size-14 w-100" href="#!">
                View All
              </a>
            </div>
          </div>
        </div>

        <div className="dropdown d-inline-block ml-2">
          <button
            type="button"
            className="btn header-item waves-effect waves-light"
            id="page-header-user-dropdown"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <img
              className="rounded-circle header-profile-user"
              src={profilePic}
              alt="Header Avatar"
            />
            <span className="ms-2 fw-semibold">{userName}</span>
            <i className="mdi mdi-chevron-down d-none d-sm-inline-block"></i>
          </button>
          <div className="dropdown-menu dropdown-menu-right">
            <button
              type="button"
              className="dropdown-item d-flex align-items-center justify-content-between"
              onClick={openProfileModal}
            >
              <span>Profile</span>
            </button>
            {canEditOrg && (
              <button
                type="button"
                className="dropdown-item d-flex align-items-center justify-content-between"
                onClick={openOrgModal}
              >
                <span>Organization Profile</span>
              </button>
            )}
            <a
              href=""
              onClick={handleLogout}
              className="dropdown-item d-flex align-items-center justify-content-between"
            >
              <span className="text-danger">Log Out</span>
            </a>
          </div>
        </div>
      </div>

      {/* Common Modal */}
      <CommonModal
        visible={modalOpen}
        title={modalType === "profile" ? "Edit Profile" : "Edit Organisation"}
        fields={modalType === "profile" ? profileFields : orgFields}
        values={formValues}
        onChange={handleFormChange}
        onCancel={() => setModalOpen(false)}
        onSubmit={handleFormSubmit}
        errors={formErrors}
      />
    </div>
  );
}

export default Nav;